import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
// `Search` removed while the Quick search box in the top bar is disabled - add it back when re-enabling
import { ChevronDown, ChevronRight, Eye, LogOut, Menu, Undo2, UserCog } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { cn } from '@/lib/cn'
import { registerServiceWorker } from '@/lib/push'
import Avatar from '@/components/ui/Avatar'
import NotificationBell from '@/components/ui/NotificationBell'
import { useNavBadges, type NavBadgeKey } from '@/hooks/useNavBadges'
import { consoleFor, profilePath } from '@/lib/roles'
import PortalBottomNav from './PortalBottomNav'
import PortalDrawer from './PortalDrawer'
import { activeNavItem, flattenNav } from './navTree'
import SideNav from './SideNav'

export interface NavItem {
  to: string
  label: string
  icon: ReactNode
  end?: boolean
  /** Other pages this item stands for, e.g. a record's own editor under an `end` list. */
  also?: RegExp
  /** Sidebar caption group; items without one fall under "Menu". */
  section?: string
  /** Kept routable and still named in the header, just off the sidebar. */
  hidden?: boolean
  /** Live count to show as a pill on the link - see useNavBadges. */
  badge?: NavBadgeKey
  /** Shorter label for PortalBottomNav, where a slot is about 75px wide. */
  tabLabel?: string
  /** The middle slot of PortalBottomNav. */
  center?: boolean
  /**
   * Sub-items. The item becomes an expanding group in the sidebar and the
   * drawer (see SideNav); its own `to` is only the key and the prefix that
   * opens it. PortalBottomNav does not render groups.
   */
  children?: NavItem[]
}

export default function PortalLayout({
  nav,
  title,
  bottomNav = false,
}: {
  nav: NavItem[]
  title: string
  /** Below `xl`, a bottom tab bar replaces the whole top bar and the drawer. For consoles with a handful of screens. */
  bottomNav?: boolean
}) {
  const { user, logout, impersonating, endImpersonation } = useAuth()
  const navRef = useRef<HTMLElement>(null)
  // True while the menu has more items hidden below the fold - drives the
  // fade + chevron scroll hint at the bottom of the sidebar.
  const [moreBelow, setMoreBelow] = useState(false)

  // Stable, because SideNav re-runs it after a group opens or closes.
  const updateScrollHint = useCallback(() => {
    const el = navRef.current
    if (!el) return
    setMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 8)
  }, [])

  useEffect(() => {
    updateScrollHint()
    window.addEventListener('resize', updateScrollHint)
    return () => window.removeEventListener('resize', updateScrollHint)
    // Re-measure when the menu itself changes length.
  }, [nav, updateScrollHint])

  // Reattach an updated sw.js on devices that already granted push; inert
  // when push was never enabled. Subscribing itself stays behind the button
  // in the bell dropdown (iOS requires the user gesture).
  useEffect(() => {
    registerServiceWorker()
  }, [])
  const navigate = useNavigate()
  const location = useLocation()

  const onLogout = async () => {
    await logout()
    navigate('/login')
  }

  // Lands on admin > Staff; the context does the swap and the move together.
  const onReturnToAdmin = () => endImpersonation()

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const drawerButtonRef = useRef<HTMLButtonElement>(null)
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false)
    drawerButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!menuOpen) return
    const onMouseDown = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  // Flattened, so a badge on a sub-item still loads.
  const badges = useNavBadges(flattenNav(nav))

  const active = activeNavItem(nav, location.pathname)
  return (
    <div className="min-h-screen before:fixed before:inset-0 before:bg-primary before:bg-noise before:content-['']">
      {/* Sidebar: no panel of its own - light text directly on the blue field */}
      <aside className="no-print fixed inset-y-0 left-0 z-40 hidden w-[300px] flex-col px-5 pt-4 xl:flex">
        <div className="flex items-center gap-3 py-3">
          <Link to="/" title="MPG marketplace">
            <img src="/logo_hd_white.png" alt="MPG Printing" className="h-6 w-auto object-contain" />
          </Link>
          {title && (
            <span className="text-2xs font-semibold tracking-wider text-background/50 border-l border-background/20 pl-2.5 uppercase">{title}</span>
          )}
        </div>

        <div className="relative min-h-0 flex-1">
          <nav ref={navRef} onScroll={updateScrollHint} className="scrollbar-thin h-full overflow-y-auto pb-8">
            <SideNav nav={nav} badges={badges} onLayoutChange={updateScrollHint} />
          </nav>

          {/* Scroll hint: the list fades out and a chevron nudges further
              down, then both vanish at the end of the menu. */}
          {moreBelow && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-primary via-primary/70 to-transparent pb-1 pt-8">
              <button
                type="button"
                aria-label="Scroll menu down"
                onClick={() => navRef.current?.scrollBy({ top: navRef.current.clientHeight * 0.6, behavior: 'smooth' })}
                className="pointer-events-auto rounded-full p-1 text-background/70 hover:text-background"
              >
                <ChevronDown className="size-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Top bar: 70px, light-on-blue */}
      <header
        className={cn(
          'no-print absolute inset-x-0 top-0 z-30 h-[70px] items-center gap-3 px-4 sm:px-7 xl:ml-[300px]',
          // The bottom bar names the page and carries My Account and Alerts,
          // so phones skip this bar and give the room to content.
          bottomNav ? 'hidden xl:flex' : 'flex',
        )}
      >
        {!bottomNav && (
          <button
            type="button"
            ref={drawerButtonRef}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            aria-controls="portal-drawer"
            className="-ml-2 flex size-10 shrink-0 items-center justify-center rounded-lg text-background/80 hover:bg-background/10 hover:text-background xl:hidden"
          >
            <Menu className="size-5 stroke-[1.5]" />
          </button>
        )}
        <nav className="flex min-w-0 items-center gap-1 text-sm text-background/70">
          <span className="hidden sm:inline">Apps</span>
          <ChevronRight className="hidden size-3.5 opacity-70 sm:block" />
          <span className="hidden sm:inline">{title}</span>
          <ChevronRight className="hidden size-3.5 opacity-70 sm:block" />
          <span className="truncate text-background">{active?.label ?? 'Home'}</span>
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-4">
          {/* Quick search is hidden until it is wired up - it was a placeholder with no behavior.
              To enable it later, uncomment this block and restore `Search` in the lucide-react import.
          <div className="hidden h-9 items-center gap-2 rounded-full border border-background/30 bg-background/10 px-4 text-background/70 sm:flex">
            <Search className="size-3.5" />
            <span className="text-xs">Quick search...</span>
          </div>
          */}
          <NotificationBell />
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Account menu"
              aria-expanded={menuOpen}
              className="flex size-9 items-center justify-center overflow-hidden rounded-full bg-background text-xs font-medium text-primary ring-2 ring-background/25"
            >
              <Avatar user={user} className="size-full" />
            </button>
            {/* .box forces position:relative (see index.css gotcha) - keep absolute on the wrapper, not the .box */}
            {menuOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-64">
                <div className="box flex flex-col gap-2.5 px-5 py-4 text-left text-foreground">
                  <div className="flex flex-col gap-0.5">
                    <p className="truncate font-medium">{user?.name}</p>
                    <p className="mt-0.5 truncate text-xs opacity-70">{user?.email}</p>
                  </div>
                  <div className="h-px bg-foreground/5" />
                  <Link
                    to={profilePath(user)}
                    onClick={() => setMenuOpen(false)}
                    className="-mx-2 flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm hover:bg-foreground/5"
                  >
                    <UserCog className="size-4 stroke-[1.5]" />
                    Account settings
                  </Link>
                  {impersonating ? (
                    <button
                      onClick={onReturnToAdmin}
                      className="-mx-2 flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm hover:bg-foreground/5"
                    >
                      <Undo2 className="size-4 stroke-[1.5]" />
                      Return to admin console
                    </button>
                  ) : (
                    <button
                      onClick={onLogout}
                      className="-mx-2 flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm hover:bg-foreground/5"
                    >
                      <LogOut className="size-4 stroke-[1.5]" />
                      Sign out
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content sheet: rounded-4xl off-white panel floating on the blue */}
      {/* With the bottom bar there is no top bar below xl: the sheet starts just
          under the status bar and ends 1rem above the 56px bar. */}
      <main
        className={cn(
          'relative min-h-screen px-3 sm:px-5 xl:ml-[300px] xl:pl-0 xl:pr-6 print:ml-0 print:p-0',
          bottomNav
            ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom))] pt-[calc(0.75rem+env(safe-area-inset-top))] xl:pb-4 xl:pt-[82px]'
            : 'pb-4 pt-[82px]',
        )}
      >
        <div
          className={cn(
            'relative rounded-[2rem] border border-foreground/15 bg-sheet px-5 py-6 shadow-[0_3px_20px_rgba(51,65,85,0.08)] sm:px-7 print:rounded-none print:border-0 print:shadow-none',
            bottomNav
              ? 'min-h-[calc(100vh-5.25rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] xl:min-h-[calc(100vh-98px)]'
              : 'min-h-[calc(100vh-98px)]',
          )}
        >
          <div className="mx-auto max-w-6xl">
            {impersonating && user && (
              // An admin "signed in as" a team member. Inside the sheet, so
              // the tint reads on the off-white rather than the blue field,
              // and on screen for the whole session so it can never be
              // mistaken for the real thing. Stacks on phones.
              <div className="no-print mb-5 flex flex-col gap-3 rounded-xl border border-warning/60 bg-warning/10 px-4 py-3 text-sm text-warning sm:flex-row sm:items-center">
                <p className="flex min-w-0 flex-1 items-start gap-2">
                  <Eye className="mt-0.5 size-4 shrink-0 stroke-[1.5]" />
                  <span>
                    <span className="font-semibold">Signed in as {user.name}</span> ({consoleFor(user).label}). You
                    are seeing exactly what they see.
                  </span>
                </p>
                <button
                  type="button"
                  onClick={onReturnToAdmin}
                  className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-warning/60 bg-warning/20 px-3 py-2 text-xs font-semibold text-warning hover:bg-warning/30"
                >
                  <Undo2 className="size-3.5" />
                  Return to admin console
                </button>
              </div>
            )}
            <Outlet />
          </div>
        </div>
      </main>

      {bottomNav ? (
        <PortalBottomNav nav={nav} />
      ) : (
        <PortalDrawer open={drawerOpen} onClose={closeDrawer} nav={nav} title={title} badges={badges} />
      )}
    </div>
  )
}
